import React, { useRef } from 'react';
import { grid, maxWidthContainer } from '@noths/polaris-client-ribbons-design-system';

import { CTAButton } from 'src/components/atoms/CTAButton';
import { TextButton } from 'src/components/atoms/TextButton';
import { useStickyNav } from 'src/components/organisms/FilterMenu/hooks/useStickyNav';
import { DisplayWhenLargeAndUp, DisplayWhenMediumAndDown } from 'src/styles/Media';
import { SelectedSortOption } from 'src/types';
import * as elementIds from '../../constants/elementIds';
import { SortByContainer } from '../../containers/SortByContainer';
import { getFilterMenuLabels } from './getFilterMenuLabels';
import * as styles from '../../styles/FilterMenu.styles';

export interface FilterMenuProps {
  openFilterModal: VoidFunction;
  openSortByModal: VoidFunction;
  onResetFilters: VoidFunction;
  totalSelectedFilterOptionsCount: number;
  totalProducts: number;
  selectedSortOption: SelectedSortOption;
}

export const FilterMenu = ({
  openFilterModal,
  openSortByModal,
  onResetFilters,
  totalSelectedFilterOptionsCount,
  totalProducts,
  selectedSortOption,
}: FilterMenuProps) => {
  const navRef = useRef<HTMLDivElement>(null);
  const stickyNav = useStickyNav(navRef.current);

  const {
    filterLabel,
    accessibleFilterLabel,
    sortLabel,
    accessibleSortLabel,
    resetAllLabel,
    accessibleResetAllLabel,
  } = getFilterMenuLabels(totalSelectedFilterOptionsCount, selectedSortOption);

  return (
    <>
      <DisplayWhenMediumAndDown css={styles.smallScreenMenuWrapper}>
        <div
          css={[styles.smallScreenMenu]}
          data-testid={elementIds.STICKY_NAV}
          data-is-sticky={stickyNav.isSticky}
          data-is-visible={stickyNav.isVisible}
          ref={navRef}
        >
          <div css={styles.smallScreenCTAWrapper}>
            <CTAButton
              data-testid={elementIds.BUTTON_OPEN_MODAL}
              onClick={openFilterModal}
              css={styles.filterButton}
              accessibleLabel={accessibleFilterLabel}
            >
              {filterLabel}
            </CTAButton>
            <CTAButton
              data-testid={elementIds.BUTTON_SORT_BY_SMALL_SCREEN}
              onClick={openSortByModal}
              css={styles.filterButton}
              accessibleLabel={accessibleSortLabel}
            >
              {sortLabel}
            </CTAButton>
          </div>
          <p css={styles.productCount}>{totalProducts} products</p>
        </div>
      </DisplayWhenMediumAndDown>
      <DisplayWhenLargeAndUp css={styles.largeScreenMenu}>
        <div css={[maxWidthContainer, grid]}>
          <div css={styles.filterBar}>
            <div css={styles.filterToolsWrapper}>
              <h2 aria-label={accessibleFilterLabel} css={styles.filterLabel}>
                {filterLabel}
              </h2>
              <TextButton
                data-testid={elementIds.BUTTON_RESET_FILTERS}
                css={styles.resetButton}
                onClick={onResetFilters}
                accessibleLabel={accessibleResetAllLabel}
              >
                {resetAllLabel}
              </TextButton>
              <p css={styles.productCount}>{totalProducts} products</p>
            </div>
            <div css={styles.sortByWrapper}>
              <SortByContainer />
            </div>
          </div>
        </div>
      </DisplayWhenLargeAndUp>
    </>
  );
};
